import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Photo } from "../components/Photo";
import { MacroBar } from "../components/MacroBar";
import { ServingsStepper } from "../components/Servings";
import { IngredientRow } from "../components/Ingredient";
import { PrepAhead } from "../components/PrepAhead";
import { RevDot } from "../components/RevDot";
import { ThemeToggle } from "../components/ThemeToggle";
import { useAuth } from "../lib/auth";
import { forkSharedRecipe, getShared, setPassStatus } from "../lib/queries";
import type { Recipe, SharedRecipe } from "../lib/types";
import { fmtDateShort, scaleText, slugify } from "../lib/format";
import { buildRecipeJsonLd, mountRecipeJsonLd } from "../lib/recipeJsonLd";

const ROMAN = ["i.", "ii.", "iii.", "iv.", "v.", "vi.", "vii.", "viii.", "ix.", "x.", "xi.", "xii."];

// Read-only public view of a shared recipe, reachable without signing in.
//   /r/:shareId         → the live head (tracks the owner's edits)
//   /r/:shareId/v/:rev  → a frozen revision
export function PublicRecipe() {
  const { shareId, rev } = useParams<{ shareId: string; rev?: string }>();
  const [params] = useSearchParams();
  const passId = params.get("pass") ?? undefined;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [share, setShare] = useState<SharedRecipe | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [servings, setServings] = useState(1);

  const pinnedRev = rev != null ? Number(rev) : null;

  useEffect(() => {
    if (!shareId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const sh = await getShared(shareId);
        if (cancelled) return;
        if (!sh) setError("This recipe link is no longer available.");
        else setShare(sh);
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? "Could not load this recipe.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shareId]);

  const exposed = share?.exposedRevisions ?? [];

  // Resolve which version to show. A pinned rev reads its frozen snapshot;
  // if the pin happens to be the current head, fall back to latestSnapshot.
  const pinned = useMemo(() => {
    if (pinnedRev == null) return undefined;
    return exposed.find((r) => r.number === pinnedRev);
  }, [exposed, pinnedRev]);

  const snap: Recipe | undefined =
    pinned?.snapshot ??
    (pinnedRev != null && pinnedRev === share?.currentRevisionNumber
      ? share?.latestSnapshot
      : undefined) ??
    (pinnedRev == null ? share?.latestSnapshot : undefined);

  useEffect(() => {
    if (snap?.servings) setServings(snap.servings);
  }, [snap?.servings]);

  // Publish the recipe as schema.org structured data. This is the page people
  // actually share, so it's the one a recipe app's share extension will be
  // pointed at — Grocery imports the ingredients straight off it.
  useEffect(() => {
    if (!snap) return;
    return mountRecipeJsonLd(
      buildRecipeJsonLd(snap, {
        url: window.location.href,
        authorName: share?.ownerName,
      })
    );
  }, [snap, share?.ownerName]);

  const ratio = useMemo(() => {
    // Fall back to a base of 1 (the stepper's baseline) when the snapshot has
    // no servings, so ingredient amounts scale instead of freezing.
    const base = snap?.servings || 1;
    return servings / base;
  }, [snap, servings]);

  async function doSave() {
    if (!share || !snap) return;
    if (!user) {
      navigate("/login");
      return;
    }
    const proposed = slugify(snap.title ?? share.title);
    const newId = window.prompt("Save to your cookbook as (slug):", proposed);
    if (!newId) return;
    setSaving(true);
    try {
      // Fork the version currently being viewed (frozen rev or live head).
      const shareForFork =
        pinned?.snapshot
          ? { ...share, latestSnapshot: pinned.snapshot, title: pinned.snapshot.title ?? share.title }
          : share;
      await forkSharedRecipe(user.uid, shareForFork, newId);
      if (passId) await setPassStatus(passId, "saved", user.uid).catch(() => {});
      navigate(`/recipe/${newId}`);
    } catch (e: any) {
      alert(e?.message ?? "Could not save that recipe.");
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <>
        <PublicBar />
        <div className="empty">
          <span className="spinner" /> &nbsp; Pulling the page…
        </div>
      </>
    );
  }
  if (error || !share || !snap) {
    return (
      <>
        <PublicBar />
        <div className="empty">
          {error ?? "Recipe not found."}
          <div style={{ marginTop: 18 }}>
            <Link to="/" className="btn">
              GO TO AFFINAGE
            </Link>
          </div>
        </div>
      </>
    );
  }

  const cookedLabel = [
    snap.category?.toUpperCase(),
    snap.servings ? `${snap.servings} SERVING${snap.servings === 1 ? "" : "S"}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const pinnedLabel = pinned
    ? `REVISION #${pinned.number}${pinned.releaseName ? ` · ${pinned.releaseName.toUpperCase()}` : ""}`
    : "LIVE VERSION";

  return (
    <>
      <PublicBar />
      <div className="fade-in">
        {/* hero */}
        <div style={{ height: 240, position: "relative" }}>
          <Photo src={snap.imageUrl} placeholder="No photo for this recipe" fill caption={false} eager />
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(13,12,10,0.85))",
            }}
          />
          <div
            className="hero-meta"
            style={{
              position: "absolute",
              left: 64,
              bottom: 22,
              right: 64,
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <div>
              <div className="eb" style={{ marginBottom: 8, color: "var(--accent)" }}>
                SERVED TO YOU · {pinnedLabel}
                {cookedLabel ? ` · ${cookedLabel}` : ""}
              </div>
              <div className="h1" style={{ fontSize: "clamp(36px, 5vw, 56px)", color: "#fff" }}>
                {snap.title}
              </div>
              {(share.ownerName || snap.subtitle) && (
                <div
                  className="subtitle-italic"
                  style={{ fontSize: 22, color: "var(--fg-muted)", marginTop: 4 }}
                >
                  {share.ownerName ? `by ${share.ownerName}` : ""}
                  {share.ownerName && snap.subtitle ? " · " : ""}
                  {snap.subtitle}
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className="btn primary" onClick={doSave} disabled={saving}>
                {saving ? "SAVING…" : user ? "↓ SAVE TO MY COOKBOOK" : "SIGN IN TO SAVE"}
              </button>
            </div>
          </div>
        </div>

        {/* spread */}
        <div
          className="recipe-spread"
          style={{
            display: "grid",
            gridTemplateColumns: exposed.length > 1 ? "0.85fr 1.2fr 0.6fr" : "0.85fr 1.4fr",
            gap: 40,
            padding: "28px 64px 64px",
          }}
        >
          {/* LEFT — ingredients */}
          <div className="recipe-col-l" style={{ borderRight: "1px solid var(--line)", paddingRight: 36 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14, flexWrap: "wrap" }}>
              <div className="eb">SERVES</div>
              <ServingsStepper value={servings} onChange={setServings} label="servings" />
            </div>
            <div className="h2" style={{ marginTop: 6, marginBottom: 12 }}>
              Ingredients
            </div>
            <MacroBar macros={snap.macros} />
            <div style={{ marginTop: 22 }}>
              {(snap.ingredientGroups ?? []).map((g, i) => (
                <div key={i}>
                  {g.label && (
                    <div
                      className="eb"
                      style={{ marginTop: i === 0 ? 0 : 16, marginBottom: 6, color: "var(--accent)" }}
                    >
                      {g.label.toUpperCase()}
                    </div>
                  )}
                  {(g.ingredients ?? []).map((ing, j) => (
                    <IngredientRow key={j} ing={ing} ratio={ratio} />
                  ))}
                </div>
              ))}
              {(!snap.ingredientGroups || snap.ingredientGroups.length === 0) && (
                <div className="muted serif-italic">No ingredients listed.</div>
              )}
            </div>
          </div>

          {/* CENTER — narrative steps */}
          <div className="recipe-col-c cookbook">
            <div className="eb" style={{ marginBottom: 8 }}>
              HOW TO MAKE IT
            </div>
            {snap.description && (
              <p
                className="dropcap"
                style={{
                  fontFamily: "var(--font-body-serif)",
                  fontSize: 15.5,
                  lineHeight: 1.75,
                  color: "var(--fg)",
                  marginTop: 0,
                }}
              >
                {snap.description}
              </p>
            )}
            <PrepAhead items={snap.prepAhead} ratio={ratio} />
            {(snap.steps ?? []).map((s, i) => (
              <div key={i} style={{ marginTop: 18 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 4, flexWrap: "wrap" }}>
                  <span
                    style={{
                      fontFamily: "var(--font-serif)",
                      fontWeight: 700,
                      fontStyle: "italic",
                      fontSize: 24,
                      color: "var(--accent)",
                    }}
                  >
                    {ROMAN[i] ?? `${i + 1}.`}
                  </span>
                  {s.title && <span className="h3">{s.title}</span>}
                  {s.timer != null && (
                    <span
                      style={{
                        marginLeft: "auto",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        color: "var(--accent)",
                      }}
                    >
                      <span>⏱</span>{" "}
                      <span className="eb-mono eb-accent">
                        {Math.floor(s.timer / 60)}:{String(s.timer % 60).padStart(2, "0")}
                      </span>
                    </span>
                  )}
                </div>
                {(s.text || s.bullets) && (
                  <div
                    style={{
                      fontFamily: "var(--font-serif)",
                      fontSize: 14,
                      color: "var(--fg-muted)",
                      lineHeight: 1.7,
                      marginLeft: 30,
                    }}
                  >
                    {scaleText(s.text, ratio)}
                    {s.bullets && s.bullets.length > 0 && (
                      <ul style={{ marginTop: 6 }}>
                        {s.bullets.map((b, k) => (
                          <li key={k}>{scaleText(b, ratio, { leading: true })}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            ))}
            {(!snap.steps || snap.steps.length === 0) && (
              <div className="muted serif-italic">No steps written yet.</div>
            )}

            {snap.notes && snap.notes.length > 0 && (
              <div style={{ marginTop: 36, paddingTop: 18, borderTop: "1px solid var(--line)" }}>
                <div className="eb" style={{ marginBottom: 12 }}>
                  NOTES
                </div>
                {snap.notes.map((n, i) => (
                  <div key={i} style={{ marginBottom: 14 }}>
                    {n.title && <div className="h4" style={{ marginBottom: 4 }}>{n.title}</div>}
                    {n.body && (
                      <div
                        style={{
                          fontFamily: "var(--font-serif)",
                          fontSize: 14,
                          color: "var(--fg-muted)",
                          lineHeight: 1.7,
                        }}
                      >
                        {n.body}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT — browse versions via the public link */}
          {exposed.length > 1 && (
            <div className="recipe-col-r">
              <div
                style={{
                  background: "var(--surface-warm)",
                  border: "1px solid var(--line)",
                  padding: "18px 18px 14px",
                  alignSelf: "start",
                }}
              >
                <div className="eb" style={{ marginBottom: 8 }}>
                  VERSIONS
                </div>
                <div className="h3" style={{ fontSize: 16, marginBottom: 14, color: "var(--fg)" }}>
                  Browse the history
                </div>
                <div>
                  <RevRow
                    n={null}
                    label="Live version"
                    sub="UPDATES AS THEY REFINE"
                    active={pinnedRev == null}
                    state="current"
                    onClick={() => navigate(`/r/${shareId}`)}
                  />
                  {[...exposed]
                    .filter((r) => r.number !== share.currentRevisionNumber)
                    .sort((a, b) => b.number - a.number)
                    .map((r) => (
                      <RevRow
                        key={r.number}
                        n={r.number}
                        label={r.releaseName || r.note || `Revision #${r.number}`}
                        sub={[
                          r.savedAt ? fmtDateShort(r.savedAt).toUpperCase() : "",
                          r.releaseName ? r.releaseName.toUpperCase() : "",
                        ]
                          .filter(Boolean)
                          .join(" · ") || `#${r.number}`}
                        active={pinnedRev === r.number}
                        state={r.releaseName ? "milestone" : "past"}
                        onClick={() => navigate(`/r/${shareId}/v/${r.number}`)}
                      />
                    ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <style>{`
        @media (max-width: 1100px) {
          .recipe-spread { grid-template-columns: 1fr 1fr !important; }
          .recipe-col-r { grid-column: 1 / -1; }
        }
        @media (max-width: 800px) {
          .recipe-spread { grid-template-columns: 1fr !important; padding: 24px 20px 48px !important; gap: 28px !important; }
          .recipe-col-l { border-right: 0 !important; padding-right: 0 !important; border-bottom: 1px solid var(--line); padding-bottom: 24px; }
          .hero-meta { left: 20px !important; right: 20px !important; }
        }
      `}</style>
    </>
  );
}

// Minimal header for the logged-out public page — just the wordmark + sign-in.
function PublicBar() {
  const { user } = useAuth();
  return (
    <header className="appbar">
      <div className="appbar-brand">
        <Link to="/">
          <span className="name">Affinage</span>
          <span className="tagline">the slow refinement of every recipe.</span>
        </Link>
      </div>
      <nav className="appbar-nav">
        {user ? (
          <Link to="/served">Served to You</Link>
        ) : (
          <Link to="/login">Sign in</Link>
        )}
      </nav>
      {/* Phones hide .appbar-nav; surface an always-visible toggle in the bar. */}
      <ThemeToggle className="appbar-toggle-mobile" />
    </header>
  );
}

function RevRow({
  n,
  label,
  sub,
  active,
  state,
  onClick,
}: {
  n: number | null;
  label: string;
  sub: string;
  active: boolean;
  state: "current" | "milestone" | "past";
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        display: "grid",
        gridTemplateColumns: "26px 1fr",
        gap: 10,
        padding: "8px 0",
        borderTop: "1px solid var(--line)",
        cursor: "pointer",
      }}
    >
      <div style={{ paddingTop: 1 }}>
        {n != null ? (
          <RevDot n={n} state={state} />
        ) : (
          <span style={{ display: "inline-block", width: 20, textAlign: "center", color: "var(--accent)" }}>
            ★
          </span>
        )}
      </div>
      <div>
        <div
          style={{
            fontFamily: "var(--font-serif)",
            fontWeight: 700,
            fontSize: 13,
            color: active ? "var(--accent)" : "var(--fg)",
            lineHeight: 1.25,
          }}
        >
          {label}
        </div>
        <div className="eb-mono" style={{ marginTop: 3 }}>
          {sub}
          {active && <span style={{ color: "var(--accent)" }}> · VIEWING</span>}
        </div>
      </div>
    </div>
  );
}
